<script setup>
// Базовый компонент с общей логикой
import { computed, ref, watchEffect, defineEmits, defineProps } from "vue";
import { convertTimestampToData } from "@/tools/date-time-tools";
import { useNotificationsStore } from "@/store/notifications";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { importanceList } from "@/const/importance.list";
import { t } from "@/controllerLK/AvalLocalization";

// UI Components
import LocationIcon from "@/components/Icons/LocationIcon.vue";
import IconTranslate from "@/components/Icons/IconTranslate.vue";
import IconTranslateFixed from "@/components/Icons/IconTranslateFixed.vue";
import LocalLoader from "@/components/UI/LocalLoader.vue";
import ArrowTopIcon from "@/components/Icons/ArrowTopIcon.vue";
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";
import CrossIconSemi from "@/components/Icons/CrossIconSemi.vue";

const props = defineProps({
  newsItem: {
    type: Object,
    required: true
  },
  cityName: {
    type: String,
    required: false,
    default: ""
  },
  activeFilterTypeOption: {
    type: String,
    required: true,
    validator: (value) => ["sentiment", "importance"].includes(value)
  },
  // Режим отображения. 1 карточка или список
  variant: {
    type: String,
    default: "card", // "card" или "standalone"
    validator: (value) => ["card", "standalone"].includes(value)
  },
  // Для режима card - возможность сворачивания текста карточки
  collapsible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["close"]);

const store = useNotificationsStore();
const newsFeedStore = useNewsFeedStore();

const isCardOpened = ref(!props.collapsible);
const isActiveTranslateNewsText = ref(false);
const isActiveNewsTextTranslated = ref(false);
const translatedNewsTitle = ref("");
const translatedNewsText = ref("");

watchEffect(() => {
  if (props.newsItem) {
    translatedNewsTitle.value = props.newsItem.title;
    translatedNewsText.value = props.newsItem.text;
  }
});

const translateNewsText = async () => {
  isActiveTranslateNewsText.value = true;
  if (isActiveNewsTextTranslated.value) {
    translatedNewsTitle.value = props.newsItem.title;
    translatedNewsText.value = props.newsItem.text;
    isActiveNewsTextTranslated.value = false;
  } else {
    const translated = await newsFeedStore.translateNewsText(
      translatedNewsTitle.value,
      translatedNewsText.value,
      props.newsItem.id
    );

    translatedNewsTitle.value = translated.title;
    translatedNewsText.value = translated.raw_text;
    isActiveNewsTextTranslated.value = true;
  }
  isActiveTranslateNewsText.value = false;
};

const saveIdToClipboard = async (id) => {
  try {
    await navigator.clipboard.writeText("id: " + id);
    store.createNotify(t("map_page.toast.clipboard_success"), "success");
  } catch (e) {
    console.log(e);
    store.createNotify(t("map_page.toast.clipboard_error"), "error");
  }
};

// Город для отображения
const displayCity = computed(() => {
  return props.cityName || props.newsItem.cities?.[0]?.city || "";
});
</script>

<template>
  <article
    class="news-card"
    :class="`news-card--${variant}`"
  >
    <!-- Header -->
    <header class="news-card__header">
      <h2 class="news-card__title">
        {{ translatedNewsTitle }}
      </h2>

      <div class="news-card__actions">
        <!-- Кнопка перевода -->
        <button
          type="button"
          class="news-card__action news-card__action--translate"
          :class="{ 'news-card__action--active': isActiveNewsTextTranslated }"
          :aria-label="isActiveNewsTextTranslated ? t('news.untranslate') : t('news.translate')"
          @click="translateNewsText"
        >
          <LocalLoader
            v-if="isActiveTranslateNewsText"
            class="news-card__loader"
          />
          <template v-else>
            <IconTranslateFixed v-if="isActiveNewsTextTranslated" />
            <IconTranslate v-else />
          </template>
        </button>

        <!-- Кнопка сворачивания (только для collapsible) -->
        <button
          v-if="collapsible"
          type="button"
          class="news-card__action news-card__action--toggle"
          :class="{ 'news-card__action--opened': isCardOpened }"
          :aria-label="isCardOpened ? t('news.collapse') : t('news.expand')"
          @click="isCardOpened = !isCardOpened"
        >
          <ArrowTopIcon v-if="isCardOpened" />
          <ArrowBottomIcon v-else />
        </button>

        <!-- Кнопка закрытия (только для standalone) -->
        <button
          v-if="variant === 'standalone'"
          type="button"
          class="news-card__action news-card__action--close"
          :aria-label="t('news.close')"
          @click="emit('close')"
        >
          <CrossIconSemi />
        </button>
      </div>
    </header>

    <!-- Content -->
    <article
      v-if="variant === 'standalone' || isCardOpened"
      class="news-card__content"
    >
      <!-- Meta информация -->
      <div class="news-card__meta">
        <time
          class="news-card__date"
          :datetime="newsItem.date"
        >
          {{ convertTimestampToData(newsItem.date) }}
        </time>

        <div class="news-card__info">
          <!-- Локация -->
          <div
            v-if="displayCity"
            class="news-card__location"
          >
            <LocationIcon class="news-card__location-icon" />
            <span class="news-card__location-text">{{ displayCity }}</span>
          </div>
          <!-- Отображение сантимента/важности в зависимости от выбранного фильтра -->
          <Sentiment
            v-if="activeFilterTypeOption === 'importance' && newsItem.sentiment !== null"
            :model-value="true"
            :type="newsItem?.sentiment > 0 ? 'good' : newsItem?.sentiment < 0 ? 'sad' : 'neutral'"
          />
          <Chip
            v-else-if="activeFilterTypeOption === 'sentiment' && newsItem.importance !== null && newsItem.importance !== 0"
            class="news-card__importance-badge"
            :model-value="true"
            :use-cross-icon="false"
            :color="importanceList[newsItem.importance].colorName"
          >
            {{ t('map_single_news_view.importance.' + importanceList[newsItem.importance].name) }}
          </Chip>
        </div>
      </div>

      <!-- Текст новости -->
      <div
        v-dompurify-html="translatedNewsText"
        class="news-card__text"
      />
    </article>

    <!-- Footer -->
    <footer class="news-card__footer">
      <!-- Источники -->
      <div class="news-card__sources">
        <div class="news-card__source">
          <span class="news-card__source-label">Feed url:</span>
          <a
            :href="newsItem.source"
            target="_blank"
            rel="noopener noreferrer"
            class="news-card__source-link"
          >
            {{ newsItem.domain }}
          </a>
        </div>

        <button
          type="button"
          class="news-card__source news-card__source--copyable"
          @click="saveIdToClipboard(newsItem.id)"
        >
          <span class="news-card__source-label">id:</span>
          <span class="news-card__source-link">{{ newsItem.id }}</span>
        </button>
      </div>

      <!-- Отображение сантимента/важности в зависимости от выбранного фильтра -->
      <div class="news-card__footer-indicator">
        <Sentiment
          v-if="activeFilterTypeOption === 'sentiment' && newsItem?.sentiment !== null"
          :model-value="true"
          :type="newsItem?.sentiment > 0 ? 'good' : newsItem?.sentiment < 0 ? 'sad' : 'neutral'"
        />
        <Chip
          v-else-if="activeFilterTypeOption === 'importance' && newsItem.importance !== null"
          class="news-card__importance-badge"
          :model-value="true"
          :use-cross-icon="false"
          :color="importanceList[newsItem.importance].colorName"
        >
          {{ t('map_single_news_view.importance.' + importanceList[newsItem.importance].name) }}
        </Chip>
      </div>
    </footer>
  </article>
</template>

<style scoped lang="scss">
@use "@/assets/style/variables.scss";

.news-card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-radius: 8px;
  background: var(--background-bg-white);
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.14);
  gap: 10px;

  // Стили для отдельной карточки
  &--standalone {
    position: absolute;
    right: 0;
    bottom: 14px;
    width: 500px;
    max-height: calc(100% - 78px);
    z-index: 999;
  }

  // Header
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  &__title {
    flex: 1;
    margin: 0;
    font-size: 16px;
    line-height: 22px;
    font-weight: 500;
    color: var(--text-text-main);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
  }

  &__action {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--button-secondary-neutral-default);
    color: var(--icon-icon-primary);
    cursor: pointer;

    svg {
      width: 17px;
      height: 17px;
    }

    &:hover {
      background-color: var(--button-secondary-neutral-hover);
      color: var(--button-secondary-neutral-hover-icon);
    }

    &--active {
      background: var(--icon-icon-primary);

      &:hover {
        background: var(--purple-purple80);
      }
    }

    &--toggle {
      &.news-card__action--opened {
        color: var(--blue-blue60);
      }
    }
  }

  &__loader {
    width: 18px;
    height: 18px;
  }

  // Content
  &__content {
    display: flex;
    flex-direction: column;
    padding: 14px 20px;
    border-radius: 8px;
    background: var(--background-01);
    gap: 10px;
    overflow-y: auto;

    // Scrollbar стили
    &::-webkit-scrollbar {
      width: 27px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: variables.$blue-another;
      border: 10px solid transparent;
      background-clip: padding-box;
      border-radius: 30px;
    }
  }

  &__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  &__date {
    font-size: 14px;
    font-weight: 400;
    color: var(--text-text-pur);
    white-space: nowrap;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
  }

  &__location {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 12px;
    border-radius: 20px;
    background: var(--background-bg-white);
    color: var(--text-text-pur);
    min-width: 0;
  }

  &__location-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &__location-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__text {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: var(--character-primary85, #373D3F);
  }

  // Footer
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  &__sources {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    min-width: 0;
  }

  &__source {
    display: flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 12px;
    font-weight: 400;
    color: var(--text-text-pur);

    &--copyable {
      padding: 0;
      border: none;
      background: none;
      cursor: pointer;
      text-align: left;

      &:hover .news-card__source-link {
        color: var(--blue-blue60);
      }
    }
  }

  &__source-label {
    flex-shrink: 0;
  }

  &__source-link {
    font-size: 12px;
    font-weight: 400;
    color: var(--text-text-on);
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 14px;
  }

  &__footer-indicator {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  &__importance-badge {
    white-space: nowrap;
    height: 24px;
  }
}
</style>